import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import DoublePointTakeoverLogo from './DoublePointTakeoverLogo'
import BodyText from 'LuxKit/Typography/BodyText'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import useDoublePointsCampaignTier from '../hooks/useDoublePointsCampaignTier'

const TabletContainer = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 1.25rem;
  }
`

const TextContent = styled.div`
  &.bronze {
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  &.silver {
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  &.gold {
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  &.platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const StyledTextButton = styled(TextButton)`
  &.tier-platinum {
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    color: ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

function DoublePointTakeoverSearchBanner() {
  const intl = useIntl()
  const [tier, tierName, , nextTierName] = useCurrentLoyaltyTier()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const nextTierNameDisplay = nextTierName ?? tierName
  const displayTier = useDoublePointsCampaignTier()

  const bannerTitle = useMemo(() => {
    if (hasLoyaltyAccount) {
      if (tier === 'platinum') {
        return <FormattedMessage
          id="PfXc0BB4P7doBKhw7S09"
          defaultMessage="Extend your <i>{nextTierNameDisplay}</i> {loyaltyProgramName} status"
          description="Search banner heading for platinum members to extend their tier status"
          values={{ nextTierNameDisplay, loyaltyProgramName, i: (chunks) => <i>{chunks}</i> }}
        />
      }
      return <FormattedMessage
        id="vdoJHUdHcEZYC_KMwfFO"
        defaultMessage="Fast-track to <i>{nextTierNameDisplay}</i> {loyaltyProgramName} status"
        description="Search banner heading for members to fast-track to next loyalty tier"
        values={{ nextTierNameDisplay, loyaltyProgramName, i: (chunks) => <i>{chunks}</i> }}
      />
    }
    return <FormattedMessage
      id="yX4QP6hXkabxgPST6jAE"
      defaultMessage="Fast-track your {loyaltyProgramName} status"
      description="Search banner heading for non-members to fast-track their loyalty status"
      values={{ loyaltyProgramName }}
    />
  }, [hasLoyaltyAccount, nextTierNameDisplay, loyaltyProgramName, tier])

  return <LayoutContainer>
    <TabletContainer>
      <Group direction="horizontal" gap={12} verticalAlign="center">
        <DoublePointTakeoverLogo size="medium" />
        <TextContent className={displayTier}>
          <VerticalSpacer gap={2}>
            <Heading variant="heading3">{bannerTitle}</Heading>
            <BodyText variant="large">
              {intl.formatMessage({
                id: 'tTfH_JfxO9hO-AgbYs_-',
                defaultMessage: 'Double status credits on all hotels, tours and cruises. Limited time only.',
                description: 'Search banner description about double status credits promotion',
              })}
            </BodyText>
          </VerticalSpacer>
        </TextContent>
      </Group>
      <StyledTextButton
        className={`tier-${displayTier}`}
        kind="secondary"
        size="medium"
        to="/double-status-credits"
      >{intl.formatMessage({
          id: 'qt_kFo1HgOhJ8fjiQM38',
          defaultMessage: 'View all offers',
          description: 'Button text to view all double status credits offers on search banner',
        })}
      </StyledTextButton>
    </TabletContainer>
  </LayoutContainer>
}

export default DoublePointTakeoverSearchBanner
